import { CircularProgress, Paper, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useLastInventorySyncDate } from './useLastInventorySyncDate';

export interface LastInventorySyncDateBadgeProps {
  dealerId: string | undefined;
  /** Optional style overrides for the outer Paper. */
  sx?: SxProps<Theme>;
}

/**
 * Displays "Last Inventory Sync Date" with loading / error / formatted value.
 * Shared by Emails grid and Inventory Feed Settings.
 */
export function LastInventorySyncDateBadge({
  dealerId,
  sx,
}: LastInventorySyncDateBadgeProps) {
  const status = useLastInventorySyncDate(dealerId);

  return (
    <Paper
      elevation={0}
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'action.hover',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        Last Inventory Sync Date
      </Typography>
      {status.isLoading ? (
        <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: status.isError ? 'error.main' : 'text.primary',
            whiteSpace: 'nowrap',
          }}
        >
          {status.displayText}
        </Typography>
      )}
    </Paper>
  );
}

export { useLastInventorySyncDate } from './useLastInventorySyncDate';
